import React, { useState } from "react";
import { LuTrash2, LuCheck, LuX, LuTable, LuColumns3, LuRows3 } from "react-icons/lu";

const TableBuilderModal = ({ isOpen, onClose, onInsert }) => {
  const [tableTitle, setTableTitle] = useState("UPSC Rank & Service Allocation — Quick Reference");
  const [tableCaption, setTableCaption] = useState("Use this as a directional reference, not as a guaranteed service cut-off.");
  const [headers, setHeaders] = useState(["Rank Range", "General Guidance", "Key Factors"]);
  const [rows, setRows] = useState([
    ["Top 100", "Generally offers broader service and cadre choices.", "Category, vacancies, preferences and eligibility"],
    ["100–250", "Can remain competitive for several Group A services.", "Year-wise vacancies and preference order"],
    ["250+", "Meaningful service opportunities can still be available.", "Allocation rules, category and available intake"],
  ]);
  const [previewTab, setPreviewTab] = useState("edit"); // edit | preview

  if (!isOpen) return null;

  // Add Column
  const handleAddColumn = () => {
    const colNumber = headers.length + 1;
    setHeaders([...headers, `Column ${colNumber}`]);
    setRows(rows.map((row) => [...row, ""]));
  };

  // Remove Column
  const handleRemoveColumn = (colIdx) => {
    if (headers.length <= 1) {
      alert("At least one column is required!");
      return;
    }
    setHeaders(headers.filter((_, idx) => idx !== colIdx));
    setRows(rows.map((row) => row.filter((_, idx) => idx !== colIdx)));
  };

  // Add Row
  const handleAddRow = () => {
    const newRow = new Array(headers.length).fill("");
    setRows([...rows, newRow]);
  };

  // Remove Row
  const handleRemoveRow = (rowIdx) => {
    if (rows.length <= 1) {
      alert("At least one row is required!");
      return;
    }
    setRows(rows.filter((_, idx) => idx !== rowIdx));
  };

  // Update Header text
  const handleHeaderChange = (idx, value) => {
    const updated = [...headers];
    updated[idx] = value;
    setHeaders(updated);
  };

  // Update Cell text
  const handleCellChange = (rIdx, cIdx, value) => {
    const updated = rows.map((r, i) => {
      if (i === rIdx) {
        const newR = [...r];
        newR[cIdx] = value;
        return newR;
      }
      return r;
    });
    setRows(updated);
  };

  // Quick preset loader
  const loadPreset = (type) => {
    if (type === "2col") {
      setHeaders(["Service / Post", "Eligibility & Criteria"]);
      setRows([
        ["IAS (Indian Administrative Service)", "Top rankers, Indian citizen, degree qualified"],
        ["IPS (Indian Police Service)", "Physical standards + high rank in civil services"],
        ["IFS (Indian Foreign Service)", "High rank preference, diplomatic aptitude"],
      ]);
    } else if (type === "3col") {
      setHeaders(["Rank Range", "General Guidance", "Key Factors"]);
      setRows([
        ["Top 100", "Generally offers broader service and cadre choices.", "Category, vacancies, preferences"],
        ["100–250", "Competitive for several Group A services.", "Preference order and vacancies"],
        ["250+", "Meaningful service opportunities available.", "Allocation rules and category"],
      ]);
    } else if (type === "4col") {
      setHeaders(["Exam Stage", "Total Papers", "Marks", "Duration"]);
      setRows([
        ["Prelims (Objective)", "2 (GS + CSAT)", "400 Marks", "2 Hours each"],
        ["Mains (Written)", "9 Papers", "1750 Marks", "3 Hours each"],
        ["Interview / PT", "1 Personality Board", "275 Marks", "Approx 30 mins"],
      ]);
    }
  };

  // Generate HTML and insert
  const handleInsertTable = () => {
    const html = `
<div class="mt-8 overflow-x-auto">
  ${tableTitle.trim() ? `<h3 class="mb-4 font-heading text-xl font-bold">${tableTitle.trim()}</h3>` : ""}
  <table class="variation-table">
    <thead>
      <tr>
        ${headers.map((h) => `<th>${h || "Column"}</th>`).join("")}
      </tr>
    </thead>
    <tbody>
      ${rows
        .map(
          (row) => `
      <tr>
        ${row.map((cell) => `<td>${cell || "-"}</td>`).join("")}
      </tr>`
        )
        .join("")}
    </tbody>
  </table>
  ${
    tableCaption.trim()
      ? `<p class="mt-3 text-xs text-muted-foreground">${tableCaption.trim()}</p>`
      : ""
  }
</div>
`;
    onInsert(html.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
      <div className="relative w-full max-w-5xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-border">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-[#114598] text-white">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-white/10 rounded-lg">
              <LuTable className="text-xl text-[#F68122]" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Dynamic Table Builder</h2>
              <p className="text-xs text-white/80">
                Create tables with any number of rows &amp; columns dynamically.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <LuX className="text-xl" />
          </button>
        </div>

        {/* Modal Subheader Controls & Quick Presets */}
        <div className="px-6 py-3 bg-[#F8FAFC] border-b border-border flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-700">Quick Presets:</span>
            <button
              type="button"
              onClick={() => loadPreset("2col")}
              className="px-2.5 py-1 bg-white border border-border rounded-lg hover:border-primary hover:text-primary transition-all font-semibold"
            >
              2 Columns
            </button>
            <button
              type="button"
              onClick={() => loadPreset("3col")}
              className="px-2.5 py-1 bg-white border border-border rounded-lg hover:border-primary hover:text-primary transition-all font-semibold"
            >
              3 Columns
            </button>
            <button
              type="button"
              onClick={() => loadPreset("4col")}
              className="px-2.5 py-1 bg-white border border-border rounded-lg hover:border-primary hover:text-primary transition-all font-semibold"
            >
              4 Columns (Exam Pattern)
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-500">
              {headers.length} Cols × {rows.length} Rows
            </span>
            <div className="bg-white border border-border rounded-lg p-0.5 flex">
              <button
                type="button"
                onClick={() => setPreviewTab("edit")}
                className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                  previewTab === "edit"
                    ? "bg-[#114598] text-white"
                    : "text-slate-600 hover:text-primary"
                }`}
              >
                Visual Editor
              </button>
              <button
                type="button"
                onClick={() => setPreviewTab("preview")}
                className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                  previewTab === "preview"
                    ? "bg-[#114598] text-white"
                    : "text-slate-600 hover:text-primary"
                }`}
              >
                Live Preview
              </button>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0 space-y-5 custom-scrollbar">
          {previewTab === "edit" ? (
            <>
              {/* Table Title & Caption Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Table Title / Heading (Optional)
                  </label>
                  <input
                    type="text"
                    value={tableTitle}
                    onChange={(e) => setTableTitle(e.target.value)}
                    placeholder="e.g. UPSC Rank & Service Allocation — Quick Reference"
                    className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Bottom Note / Caption (Optional)
                  </label>
                  <input
                    type="text"
                    value={tableCaption}
                    onChange={(e) => setTableCaption(e.target.value)}
                    placeholder="e.g. Use this as a directional reference..."
                    className="w-full px-3 py-2 border border-border rounded-xl text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Action Buttons: Add Column & Add Row */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleAddColumn}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-50 border border-blue-200 text-[#114598] rounded-xl text-xs font-bold hover:bg-blue-100 transition-colors cursor-pointer"
                >
                  <LuColumns3 className="text-base" /> + Add New Column
                </button>
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-xs font-bold hover:bg-emerald-100 transition-colors cursor-pointer"
                >
                  <LuRows3 className="text-base" /> + Add New Row
                </button>
                <span className="text-xs text-muted-foreground ml-auto hidden sm:inline">
                  Tip: You can edit column headers and cell values directly in the grid below.
                </span>
              </div>

              {/* Editable Grid Table */}
              <div className="overflow-x-auto border border-border rounded-xl bg-white shadow-xs">
                <table className="w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="bg-[#114598] text-white">
                      <th className="p-3 w-12 text-center text-xs font-bold uppercase tracking-wider text-white/70">
                        #
                      </th>
                      {headers.map((header, cIdx) => (
                        <th key={cIdx} className="p-3 min-w-[200px]">
                          <div className="flex items-center justify-between gap-1.5">
                            <input
                              type="text"
                              value={header}
                              onChange={(e) => handleHeaderChange(cIdx, e.target.value)}
                              placeholder={`Header ${cIdx + 1}`}
                              className="w-full px-2 py-1 text-xs font-bold bg-white/10 text-white rounded border border-white/20 focus:outline-none focus:bg-white focus:text-slate-900 placeholder-white/50"
                            />
                            {headers.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveColumn(cIdx)}
                                title="Delete this column"
                                className="p-1 hover:bg-red-500/80 rounded text-white/80 hover:text-white transition-colors cursor-pointer"
                              >
                                <LuTrash2 className="text-sm" />
                              </button>
                            )}
                          </div>
                        </th>
                      ))}
                      <th className="p-3 w-16 text-center text-xs">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rIdx) => (
                      <tr
                        key={rIdx}
                        className={rIdx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}
                      >
                        <td className="p-3 text-center text-xs font-bold text-slate-400 border-t border-border">
                          {rIdx + 1}
                        </td>
                        {row.map((cell, cIdx) => (
                          <td key={cIdx} className="p-2 border-t border-border">
                            <textarea
                              rows={2}
                              value={cell}
                              onChange={(e) => handleCellChange(rIdx, cIdx, e.target.value)}
                              placeholder={`Row ${rIdx + 1}, Col ${cIdx + 1}`}
                              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-slate-200 focus:ring-1 focus:ring-primary focus:outline-none resize-y bg-white"
                            />
                          </td>
                        ))}
                        <td className="p-2 text-center border-t border-border">
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(rIdx)}
                              title="Delete this row"
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              <LuTrash2 className="text-base" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* Live Table Preview */
            <div className="bg-[#F8FAFC] p-6 rounded-2xl border border-border">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                Landing Page Preview:
              </p>
              <div className="bg-white p-6 rounded-xl border border-border shadow-xs">
                {tableTitle && (
                  <h3 className="mb-4 font-heading text-xl font-bold text-[#114598]">
                    {tableTitle}
                  </h3>
                )}
                <div className="overflow-x-auto">
                  <table className="variation-table">
                    <thead>
                      <tr>
                        {headers.map((h, i) => (
                          <th key={i}>{h || `Column ${i + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, rIdx) => (
                        <tr key={rIdx}>
                          {row.map((cell, cIdx) => (
                            <td key={cIdx}>{cell || "-"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {tableCaption && (
                  <p className="mt-3 text-xs text-muted-foreground">{tableCaption}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-[#F8FAFC] border-t border-border flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Total {headers.length} columns &amp; {rows.length} rows will be added to the blog.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-100 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleInsertTable}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#114598] hover:bg-[#F68122] text-white rounded-xl text-xs font-bold transition-all shadow-md cursor-pointer"
            >
              <LuCheck className="text-base" /> Insert Table into Blog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableBuilderModal;
